/**
 * 권한 칩 색상 — 백엔드 PermissionCode 의 <b>domain</b> 필드를 기준으로 색을 매긴다.
 *
 * 왜 도메인 기준인가
 *   예전엔 여기서 권한 코드를 직접 나열해 색을 매겼는데(HR_READ·FINANCE_MANAGE …),
 *   백엔드에서 권한이 도메인별로 세분화되면서 그 코드들이 사라졌다. 프론트만 옛 이름을 들고 있어서
 *   실제 HR_*·FINANCE_* 30여 개가 전부 "기타" 회색 칩으로 떨어져 있었다.
 *   코드 목록을 두 군데서 관리하면 반드시 이렇게 어긋나므로, 분류는 백엔드가 내려주는
 *   domain 을 그대로 쓴다 — 권한 코드가 추가돼도 프론트는 고칠 게 없다.
 *
 * 새 도메인이 생겼을 때만
 *   PermissionCode 에 새 domain 문자열이 등장하면 아래 DOMAIN_STYLES 에 한 줄 추가한다.
 *   빠뜨려도 깨지지 않고 FALLBACK(회색)으로 표시된다.
 */

/** 도메인 → 칩 색상. 키는 PermissionCode.domain 문자열과 정확히 일치해야 한다. */
const DOMAIN_STYLES = {
    "시스템":      { bg: "#fef2f2", color: "#b91c1c", border: "#fca5a5" }, // 전체 관리자 — 가장 강한 경고색
    "관리자":      { bg: "#eff6ff", color: "#1d4ed8", border: "#93c5fd" },
    "인사팀":      { bg: "#fdf4ff", color: "#a21caf", border: "#e879f9" },
    "재무":        { bg: "#fff7ed", color: "#c2410c", border: "#fdba74" },
    "결재":        { bg: "#f0fdfa", color: "#0f766e", border: "#5eead4" },
    "메일":        { bg: "#eef2ff", color: "#4338ca", border: "#a5b4fc" },
    "나라장터":     { bg: "#ecfeff", color: "#0e7490", border: "#67e8f9" },
    "LED 포토존":  { bg: "#fefce8", color: "#a16207", border: "#fde047" },
    "비품":        { bg: "#f0fdf4", color: "#15803d", border: "#86efac" },
    "차계부":      { bg: "#fff1f2", color: "#be123c", border: "#fda4af" },
};

const FALLBACK = { bg: "#f1f5f9", color: "#64748b", border: "#e2e8f0" };

/**
 * 도메인 이름으로 칩 색상을 얻는다.
 * @param {string} domain - PermissionCode.domain (예: "재무")
 */
export const getDomainStyle = (domain) => DOMAIN_STYLES[domain] ?? FALLBACK;

/**
 * 권한 목록 응답으로 "코드 → 색상" 해석기를 만든다.
 *
 * <p>칩을 그리는 쪽은 보통 코드 문자열만 갖고 있으므로(부서 권한 목록 등), 이미 받아둔
 * 권한 목록에서 code → domain 을 만들어 준다. 목록이 아직 안 왔으면 전부 FALLBACK.
 *
 * @param {Array<{code?: string, codeEnum?: string, domain?: string}>} perms
 *        /api/permissions 또는 /api/permissions/list 응답 (두 DTO 의 코드 필드명이 달라 둘 다 받는다)
 * @returns {(code: string) => {bg: string, color: string, border: string}}
 */
export const makeCodeStyleResolver = (perms) => {
    const norm = (v) => String(v ?? "").trim().toUpperCase();
    const codeToDomain = {};
    for (const p of Array.isArray(perms) ? perms : []) {
        const code = norm(p?.codeEnum ?? p?.code);
        if (code) codeToDomain[code] = p?.domain;
    }
    return (code) => getDomainStyle(codeToDomain[norm(code)]);
};
